import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Input, Form } from "antd";
import { PasswordPolicy } from "@companion-core/shared/app/Interfaces/validators";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import InputLabel from "@companion-core/web/src/Components/InputLabel";
import { JPInputError } from "@companion-core/shared/app/Components/Inputs/JPInputError";
import { getConfig } from "@companion-core/shared/app/Utils/config";
import { isStrongPassword, isRegexMatched } from "@companion-core/shared/app/Utils/validators";
import { JPInputPasswordProps } from "./jpInputPassword.model";
import "@companion-core/web/src/Assets/Styles/scss/inputpassword.scss";

export const JPInputPassword: React.FC<JPInputPasswordProps> = ({
  name,
  payloadPath = name,
  required = false,
  label,
  placeholder,
  showPolicies = false,
  confirmationPassword = false,
  testID,
  disableValidation = false,
  inputAccessibilityLabel,
  confirmationInputAccessibilityLabel,
  setFieldErrors,
  onValueChange,
  registerValidation,
}: JPInputPasswordProps) => {
  const form = Form.useFormInstance(); // Get current context form instance
  const { t } = useTranslation();
  const [password, setPassword] = useState<string>("");
  const passwordRef = useRef(password); // Create a ref to store the latest value and avoid stale closure
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>("");
  const passwordConfirmationRef = useRef(passwordConfirmation); // Create a ref to store the latest value and avoid stale closure
  const [errorPassword, setErrorPassword] = useState<string>("");
  const [errorConfirmation, setErrorConfirmation] = useState<string>("");
  const policies: PasswordPolicy[] = getConfig()?.options.policies.password ?? [];
  const [errorLabel, setErrorLabel] = useState<string | undefined>(undefined);
  const inputPasswordLabel = label ? t(label) : "";
  const inputPasswordId = `${name}--password`;
  const inputPasswordConfirmationId = `${name}--password-confirmation`;
  const inputPasswordConfirmationLabel = t("password.resetV2.confirmation.title");
  const showPasswordAriaLabel = t("common.form.input.password.actions.show");
  const hidePasswordAriaLabel = t("common.form.input.password.actions.hide");
  const clearPasswordAriaLabel = t("common.form.input.password.actions.clear");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const renderVisibilityToggle = (
    visible: boolean,
    iconSize: number,
    testId: string,
    onToggle: () => void
  ) => (
    <button
      type="button"
      className="input-password__visibility-toggle"
      aria-label={visible ? hidePasswordAriaLabel : showPasswordAriaLabel}
      aria-pressed={visible}
      onClick={onToggle}>
      <FontIcon
        size={iconSize}
        name={visible ? "JP-View" : "JP-Hide"}
        id={`${testId}--visibility-icon`}
        data-testid={`${testId}--${visible ? "show" : "hide"}-password`}
        className="input-password__icon"
        aria-hidden="true"
      />
    </button>
  );
  const renderClearButton = (iconSize: number, testId: string, onClear: () => void) => (
    <button
      type="button"
      className="input-password__clear-button"
      aria-label={clearPasswordAriaLabel}
      onMouseDown={(e) => e.preventDefault()} // Keep input focus (avoid premature blur validation)
      onClick={onClear}>
      <FontIcon
        size={iconSize}
        name="JP-Circle-close-outline"
        data-testid={`${testId}--clear`}
        className="input-password__icon"
        aria-hidden="true"
      />
    </button>
  );

  useEffect(() => {
    if (registerValidation) {
      registerValidation(name, () => {
        handleOnBlur();
        if (confirmationPassword) {
          handleOnBlurConfirmation();
        }
      });
    }
  }, []);

  useEffect(() => {
    // Update refs value
    passwordRef.current = password;
    passwordConfirmationRef.current = passwordConfirmation;
  }, [password, passwordConfirmation]);

  const handleOnBlur = () => {
    const err =
      !passwordRef.current && required
        ? "common.form.input.error.required"
        : !disableValidation && !isStrongPassword(policies, passwordRef.current)
          ? "common.form.input.validationErrors.password.policies"
          : "";
    setErrorPassword(err);
    if (setFieldErrors) {
      const errors = [err, errorConfirmation].filter((element) => Boolean(element));
      setFieldErrors(errors);
    }
  };

  const handleOnBlurConfirmation = () => {
    const err =
      !passwordConfirmationRef.current && required
        ? "common.form.input.error.required"
        : passwordRef.current !== passwordConfirmationRef.current
          ? "password.resetV2.confirmation.passwordConfirmation.error.invalid"
          : "";
    setErrorConfirmation(err);
    if (setFieldErrors && err) {
      const errors = [errorPassword, err].filter((element) => Boolean(element));
      setFieldErrors(errors);
    }
  };

  useEffect(() => {
    setErrorLabel(
      errorConfirmation
        ? inputPasswordConfirmationLabel
        : errorPassword
          ? inputPasswordLabel
          : undefined
    );
  }, [errorConfirmation, errorPassword]);

  useEffect(() => {
    if (form && payloadPath) {
      form.setFieldsValue({
        [payloadPath]: password,
      });
    }
    if (onValueChange) {
      onValueChange(password);
    }
  }, [onValueChange, payloadPath, form, password]);

  return (
    <div>
      <div className="input-password">
        {label && <InputLabel label={inputPasswordLabel} htmlFor={inputPasswordId} />}
        <Input
          type={passwordVisible ? "text" : "password"}
          {...(inputAccessibilityLabel ? { "aria-label": inputAccessibilityLabel } : {})}
          id={inputPasswordId}
          data-testid={testID}
          placeholder={placeholder ?? t("login.placeholders.password")}
          status={errorPassword ? "error" : undefined}
          onChange={(e) => setPassword(e.target.value)}
          value={password}
          onBlur={handleOnBlur}
          suffix={
            <span className="input-password__actions">
              {renderVisibilityToggle(passwordVisible, 24, testID, () =>
                setPasswordVisible((visible) => !visible)
              )}
              {password && renderClearButton(24, testID, () => setPassword(""))}
            </span>
          }
          aria-atomic="true"
        />
        {errorPassword && (
          <JPInputError
            inputLabel={label ? t(label) : undefined}
            testID={testID}
            error={errorPassword}
          />
        )}
      </div>
      {confirmationPassword && (
        <div className="input-password-confirmation">
          {label && (
            <InputLabel
              label={inputPasswordConfirmationLabel}
              htmlFor={inputPasswordConfirmationId}
            />
          )}
          <Input
            type={confirmationVisible ? "text" : "password"}
            {...(confirmationInputAccessibilityLabel
              ? { "aria-label": confirmationInputAccessibilityLabel }
              : {})}
            id={inputPasswordConfirmationId}
            data-testid={`${testID}--confirmation`}
            placeholder={t("login.placeholders.password")}
            status={errorConfirmation ? "error" : undefined}
            onChange={(e) => setPasswordConfirmation(e.target.value)}
            value={passwordConfirmation}
            onBlur={handleOnBlurConfirmation}
            suffix={
              <span className="input-password__actions">
                {renderVisibilityToggle(confirmationVisible, 20, `${testID}--confirmation`, () =>
                  setConfirmationVisible((visible) => !visible)
                )}
                {passwordConfirmation &&
                  renderClearButton(20, `${testID}--confirmation`, () =>
                    setPasswordConfirmation("")
                  )}
              </span>
            }
          />
          {errorConfirmation && (
            <JPInputError inputLabel={errorLabel} testID={testID} error={errorConfirmation} />
          )}
        </div>
      )}

      {showPolicies && (
        <div className="d-flex flex-direction-column justify-content-flex-start margin-top-24">
          {policies.map((policy: PasswordPolicy, index: number) => {
            return (
              <div key={index} className="d-flex align-items-center margin-bottom-12">
                {isRegexMatched(policy.regex, password) ? (
                  <FontIcon
                    size={20}
                    name="JP-Radio-Check"
                    className="input-password__icon-check"
                  />
                ) : (
                  <FontIcon
                    size={20}
                    name="JP-Circle-close"
                    className="input-password__icon-cancel"
                  />
                )}
                <span className="input-password__policy-label body-text-8">{t(policy.name)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
